import React, { useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { subscribeToAuthChanges, signInWithEmail } from '../services/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { AuthContext } from './AuthContext';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    const switchUser = (uid: string) => {
        localStorage.setItem('evenly_mock_user_id', uid);
        window.location.reload();
    };

    // Wrapper for consistency
    const handleSignIn = async (email: string, pass: string) => {
        const user = await signInWithEmail(email, pass);
        return user;
    };

    const syncProfile = async (firebaseUser: User) => {

        const userRef = doc(db, 'users', firebaseUser.uid);
        const publicRef = doc(db, 'public_profiles', firebaseUser.uid);

        try {
            const docSnap = await getDoc(userRef);
            if (!docSnap.exists()) {
                const userData = {
                    uid: firebaseUser.uid,
                    displayName: firebaseUser.displayName ?? `User ${firebaseUser.uid.substring(0, 4)}`,
                    email: firebaseUser.email,
                    photoURL: firebaseUser.photoURL,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    isAnonymous: firebaseUser.isAnonymous
                };
                await setDoc(userRef, userData);

                // Ensure Public Profile Exists
                await setDoc(publicRef, {
                    uid: userData.uid,
                    displayName: userData.displayName,
                    photoURL: userData.photoURL
                });

            } else {
                // Update both just in case
                await setDoc(userRef, {
                    updatedAt: serverTimestamp(),
                    email: firebaseUser.email
                }, { merge: true });

                // Self-heal: Ensure public profile exists or update it
                // We use setDoc with merge to create if missing or update if present
                await setDoc(publicRef, {
                    uid: firebaseUser.uid,
                    displayName: firebaseUser.displayName ?? (docSnap.data() as { displayName?: string }).displayName ?? `User ${firebaseUser.uid.substring(0, 4)}`,
                    photoURL: firebaseUser.photoURL ?? (docSnap.data() as { photoURL?: string }).photoURL ?? null
                }, { merge: true });
            }
        } catch (e) {
            console.error("Error syncing profile:", e);
        }
    };

    useEffect(() => {
        const unsubscribe = subscribeToAuthChanges(async (currentUser: User | null) => {
            try {
                if (currentUser) {
                    setUser(currentUser);
                    await syncProfile(currentUser);
                } else {
                    setUser(null);
                }
                setLoading(false);
            } catch (error) {
                console.error("Authentication error:", error);
                setLoading(false);
            }
        });

        return () => { unsubscribe(); };
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, switchUser, signInWithEmail: handleSignIn }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
